import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface LiquidationRiskData {
  id: string;
  protocol: string;
  chain: string;
  total_borrowed_usd: number;
  total_supplied_usd: number;
  utilization_rate: number;
  liquidations_24h_usd: number;
  liquidations_24h_count: number;
  at_risk_positions_usd: number;
  at_risk_positions_count: number;
  health_factor_avg: number;
  top_at_risk_positions: any[];
  recorded_at: string;
}

export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

export const getRiskLevel = (utilizationRate: number): RiskLevel => {
  if (utilizationRate >= 90) return 'critical';
  if (utilizationRate >= 80) return 'high';
  if (utilizationRate >= 70) return 'medium';
  return 'low';
};

export const getRiskColor = (level: RiskLevel): string => {
  switch (level) {
    case 'critical': return 'text-red-500';
    case 'high': return 'text-orange-500';
    case 'medium': return 'text-yellow-500';
    case 'low': return 'text-green-500';
    default: return 'text-muted-foreground';
  }
};

export const getRiskBadgeVariant = (level: RiskLevel): 'destructive' | 'default' | 'secondary' | 'outline' => {
  switch (level) {
    case 'critical': return 'destructive';
    case 'high': return 'destructive';
    case 'medium': return 'default';
    case 'low': return 'secondary';
    default: return 'outline';
  }
};

export const useLiquidationRisk = (protocol?: string) => {
  return useQuery({
    queryKey: ['liquidation-risk', protocol],
    queryFn: async (): Promise<LiquidationRiskData[]> => {
      let query = supabase
        .from('liquidation_risk_cache')
        .select('*')
        .order('recorded_at', { ascending: false });

      if (protocol) {
        query = query.eq('protocol', protocol);
      }

      // Get latest record per protocol
      const { data, error } = await query.limit(20);

      if (error) {
        console.error('Error fetching liquidation risk:', error);
        throw error;
      }

      // Group by protocol and get latest for each
      const byProtocol: Record<string, LiquidationRiskData> = {};
      for (const row of data as LiquidationRiskData[]) {
        if (!byProtocol[row.protocol]) {
          byProtocol[row.protocol] = row;
        }
      }

      return Object.values(byProtocol);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 15 * 60 * 1000, // 15 minutes
  });
};

export const useLiquidationRiskByProtocol = (protocol: string) => {
  return useQuery({
    queryKey: ['liquidation-risk', protocol],
    queryFn: async (): Promise<LiquidationRiskData | null> => {
      const { data, error } = await supabase
        .from('liquidation_risk_cache')
        .select('*')
        .eq('protocol', protocol)
        .order('recorded_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No data found
          return null;
        }
        console.error('Error fetching liquidation risk:', error);
        throw error;
      }

      return data as LiquidationRiskData;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useProtocolUtilizationHistory = (protocol: string, days: number = 7) => {
  return useQuery({
    queryKey: ['utilization-history', protocol, days],
    queryFn: async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('liquidation_risk_cache')
        .select('utilization_rate, recorded_at')
        .eq('protocol', protocol)
        .gte('recorded_at', startDate.toISOString())
        .order('recorded_at', { ascending: true });

      if (error) {
        console.error('Error fetching utilization history:', error);
        throw error;
      }

      return data.map(d => ({
        date: d.recorded_at,
        utilization: d.utilization_rate
      }));
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
};
